/**
 * The CRM reseller wall — one choke point for rendering reseller information.
 *
 * The requirement (2026-06-15): when a staff member lacks reseller access, they
 * must see NO reseller information ANYWHERE in the CRM — not just have the
 * Resellers PAGE hidden + route-guarded, but every reseller name / column /
 * selector on every OTHER reachable page redacted (the customer agreement
 * panel's "Reseller" stat, the prospect overview "Reseller" row, the Prospects
 * list "Reseller" column + its "Assign Reseller" bulk action, the
 * reseller picker inside the Customer/Prospect create-edit forms, the
 * "Resellers" group in global search). This is the exact analogue of the
 * financial wall (components/money.tsx): one all-or-nothing data class, gated in
 * ONE place so a page never has to remember.
 *
 * Use these everywhere a reseller is shown on a CRM surface a non-reseller role
 * can reach:
 *
 *   - `<ResellerOnly>…</ResellerOnly>`  — hide a whole reseller block (a stat
 *                                          tile, a labeled detail row, a form
 *                                          field, a table column cell).
 *   - `useResellerAccess()`             — the raw boolean (gate a table column
 *                                          header/cell pair, filter a results
 *                                          array, short-circuit a fetch).
 *
 * The gate keys on `resellers:access`, which the dashboard grants ONLY to a
 * holder of the `crm:resellers` permission (or super_admin) — and which it
 * STRIPS from any upstream/cached crm-api session for a non-holder (see
 * server/src/auth/crmSessionPermissions.ts → mergeCrmSessionPermissions, the
 * "governed page" rule). The same string drives the Resellers nav item + route
 * guard, so the page and its cross-cutting fields share one source of truth. The
 * minted CRM token additionally carries `crm_resellers` so crm-api can omit the
 * reseller association server-side (defense in depth). This module is the LAST
 * line — the one that catches a reseller field that slipped onto an
 * otherwise-allowed page.
 *
 * Pinned by reseller.test.tsx and the reseller-wall safety-net test
 * (reseller-wall.test.ts), which fails CI if a shared customer/prospect panel
 * stops self-gating reseller rendering.
 */
import React from "react";

import usePermissions from "@crm-src/hooks/usePermissions";

/**
 * True when the current CRM viewer may see reseller information. Thin wrapper
 * over `usePermissions().canAccessResellers()` so the admin bypass + permission
 * logic lives in one place (mirrors `useFinancialAccess` in components/money).
 */
export function useResellerAccess(): boolean {
  const { canAccessResellers } = usePermissions();
  return canAccessResellers();
}

export interface ResellerOnlyProps {
  children: React.ReactNode;
  /** Rendered instead of `children` when the viewer lacks reseller access.
   * Defaults to nothing (the block disappears entirely). */
  fallback?: React.ReactNode;
}

/**
 * Renders its children only for a viewer with reseller access. Use to drop a
 * whole reseller block — a stat tile, a labeled detail row, a form field, a
 * table cell — rather than trusting each page to remember the permission.
 */
export function ResellerOnly({
  children,
  fallback = null,
}: ResellerOnlyProps): React.ReactElement {
  const allowed = useResellerAccess();
  return <>{allowed ? children : fallback}</>;
}
